import { useEffect, useRef, useState } from 'react';
import { ProductInfo } from '../../../shared/types/productInfo.ts';
import { Input } from '../../components/input.tsx';
import { trpc } from '../../utils/trpc/trpc.ts';
import { durationToMs, msToDuration } from './convertCycleTime.ts';
import { FormState } from './formState.ts';


interface Props {
    partName: string;
    productInfo?: ProductInfo | null;
    readOnly?: boolean;
    // onSubmit: (productInfo: ProductInfo) => void;
}

export function ProductInfoForm({
    partName,
    productInfo,
    readOnly = false,
    // onSubmit,
}: Props) {
    const formRef = useRef<HTMLFormElement>(null);
    const [formData, setFormData] = useState<FormState>({});


    const mutateProductInfo = trpc.products.setProductInfo.useMutation();

    useEffect(() => {
        setFormData({
            produceAtNight: productInfo?.produceAtNight ?? false,
            targetCountDay: productInfo?.targetCountDay?.toString() ?? '',
            targetCountNight: productInfo?.targetCountNight?.toString() ?? '',
            targetCycleTime: productInfo?.targetCycleTime != null
                ? msToDuration(productInfo.targetCycleTime)
                : '',
        });
    }, [productInfo]);


    function handleFieldChange(
        field: keyof FormState,
        value: string | boolean
    ) {
        setFormData({
            ...formData,
            [field]: value,
        });
    }

    async function handleSubmit(event: React.FormEvent) {
        event.preventDefault();

        let targetCycleTime: number | undefined;
        if (formData.targetCycleTime) {
            try {
                targetCycleTime = durationToMs(formData.targetCycleTime);
            } catch (error) {
                alert('Invalid target cycle time. Expected format: 2m30s');
                return;
            }
        }

        const newProductInfo: ProductInfo = {
            partName,
            ...(productInfo?.partRevision && { partRevision: productInfo.partRevision }),
            ...(productInfo?.createdAt && { createdAt: productInfo.createdAt }),
            produceAtNight: formData.produceAtNight ?? false,
            ...(formData.targetCountDay && { targetCountDay: Number.parseInt(formData.targetCountDay) }),
            ...(formData.targetCountNight && { targetCountNight: Number.parseInt(formData.targetCountNight) }),
            ...(formData.targetCycleTime && { targetCycleTime }),
        };
        if (Number.isNaN(newProductInfo.targetCountDay)) {
            alert('Target count day must be a valid number');
            return;
        }
        if (Number.isNaN(newProductInfo.targetCountNight)) {
            alert('Target count night must be a valid number');
            return;
        }

        // onSubmit(formDataToSubmit);
        try {
            await mutateProductInfo.mutateAsync(newProductInfo);
        } catch (error) {
            console.error('Failed to submit product info:', error);
            alert('Failed to submit product info.');
            return;
        }
    }


    return (
        <form
            ref={formRef}
            style={{ width: '500px' }}
            onSubmit={handleSubmit}
        >
            <table>
                <tbody>
                    <tr>
                        <td>Produce at night</td>
                        <td>
                            <input
                                style={{ width: '100%' }}
                                type='checkbox'
                                checked={formData.produceAtNight ?? false}
                                onChange={(e) => !readOnly && handleFieldChange('produceAtNight', e.target.checked)}
                            />
                        </td>
                    </tr>

                    <tr>
                        <td>Target count day</td>
                        <td>
                            <Input
                                style={{ width: '100%' }}
                                type='number'
                                value={formData.targetCountDay ?? ''}
                                onChange={(e) => handleFieldChange('targetCountDay', e.target.value)}
                                readOnly={readOnly}
                            />
                        </td>
                    </tr>

                    <tr>
                        <td>Target count night</td>
                        <td>
                            <Input
                                style={{ width: '100%' }}
                                type='number'
                                value={formData.targetCountNight ?? ''}
                                onChange={(e) => handleFieldChange('targetCountNight', e.target.value)}
                                readOnly={readOnly}
                            />
                        </td>
                    </tr>

                    <tr>
                        <td>Target cycle time</td>
                        <td>
                            <input
                                style={{ width: '100%' }}
                                type='string'
                                value={formData.targetCycleTime ?? ''}
                                onChange={(e) => handleFieldChange('targetCycleTime', e.target.value)}
                                readOnly={readOnly}
                            />
                        </td>
                    </tr>
                </tbody>
            </table>

            {!readOnly && <button
                type='submit'
                style={{ width: '100%', marginTop: '8px' }}
                disabled={mutateProductInfo.isPending}
                children={mutateProductInfo.isPending ? 'Saving...' : 'Save'}
            />}
        </form>
    )
}
